import { List, ListItem, Toggle } from 'framework7-react';
import { FC, useMemo } from 'react';

import * as dayjs from 'dayjs';

import { formatMonthlyDay } from '@utilities/formatMonthlyDay';
import { RecurringSchedule, getNextRecurringEventDate } from '@utilities/getNextRecurringEventDate';

import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

interface RecurringScheduleFieldProps {
  eventProperties: Partial<{
    isRecurring: boolean;
    startDate: string;
    startTime: string;
    recurringSchedule: RecurringSchedule;
  }>;
  onUpdateEventProperties: (updatedEventProperties: {
    isRecurring?: boolean;
    recurringSchedule?: RecurringSchedule;
  }) => void;
}

export const RecurringScheduleField: FC<RecurringScheduleFieldProps> = ({
  eventProperties,
  onUpdateEventProperties,
}) => {
  const repeatingScheduleOptions = useMemo(() => {
    const { startDate, isRecurring = false } = eventProperties;

    if (!startDate || !isRecurring) {
      return [];
    }

    const startDateDay = dayjs(startDate).format('dddd');

    return [
      { label: `Weekly, every ${startDateDay}`, value: 'weekly' },
      { label: `Bi-weekly, every other ${startDateDay}`, value: 'bi-weekly' },
      { label: `Monthly, every ${formatMonthlyDay(startDate)}`, value: 'monthlyNthDay' },
    ];
  }, [eventProperties?.startDate, eventProperties?.isRecurring]);

  const endDateOptions = useMemo(() => {
    const { startDate, recurringSchedule } = eventProperties;

    const dates =
      startDate && recurringSchedule
        ? Array.from({ length: 20 }).reduce<string[]>((acc) => {
            const prevDate = acc[acc.length - 1];
            const referenceDate = prevDate
              ? dayjs(prevDate).add(1, 'day').format('YYYY-MM-DD')
              : startDate;
            const nextStartDate = getNextRecurringEventDate(
              {
                startDate,
                recurringSchedule,
              },
              referenceDate,
            );

            if (nextStartDate) {
              acc.push(nextStartDate);
            }

            return acc;
          }, [])
        : [];

    return [
      { label: 'No end date', value: '' },
      ...dates.map((date) => ({
        label: dayjs(date).format('MMMM D, YYYY'),
        value: date,
      })),
    ];
  }, [eventProperties?.startDate, eventProperties?.recurringSchedule]);

  const hasStart = Boolean(eventProperties?.startDate && eventProperties?.startTime);

  if (!hasStart) {
    return <></>;
  }

  const handleUpdateEndDate = (updatedEndDate: string) => {
    onUpdateEventProperties({
      recurringSchedule: {
        type: eventProperties?.recurringSchedule?.type ?? 'weekly',
        endDate: updatedEndDate,
      },
    });
  };

  return (
    <>
      <div style={{ margin: '0 -.5rem' }}>
        <List className="m-0">
          <ListItem title="Repeating Event">
            <div slot="media">
              <div style={{ width: 18 }}>
                <FontAwesomeIcon name="redo" />
              </div>
            </div>
            <Toggle
              slot="after"
              defaultChecked={Boolean(eventProperties?.recurringSchedule?.type)}
              onToggleChange={(isRecurring: boolean) =>
                onUpdateEventProperties({
                  isRecurring,
                  recurringSchedule: eventProperties?.recurringSchedule ?? {
                    type: 'weekly',
                  },
                })
              }
            />
          </ListItem>
        </List>
      </div>
      {eventProperties.isRecurring && (
        <>
          <Select
            name="recurringScheduleType"
            options={repeatingScheduleOptions}
            value={eventProperties?.recurringSchedule?.type}
            onChange={(e) =>
              onUpdateEventProperties({
                recurringSchedule: {
                  type: e.target.value as 'weekly' | 'bi-weekly' | 'monthlyNthDay',
                },
              })
            }
          />
          <Select
            name="finalDate"
            options={endDateOptions}
            value={eventProperties?.recurringSchedule?.endDate}
            onChange={(e) => handleUpdateEndDate(e.target.value)}
          />
        </>
      )}
    </>
  );
};
